import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Save as SaveIcon,
  AutoAwesome as AutoAwesomeIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';

interface BlurbDetail {
  // Prompt fields
  prompt_id: string;
  prompt_name: string;
  prompt: string;
  good_example: string | null;
  bad_example: string | null;
  process_order: number;

  // Blurb fields
  blurb_record_id: number | null;
  blurb_prompt: string | null;
  blurb: string | null;
  deliverable_property_id: number | null;
}

export const BlurbDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { appraisalId, promptId } = useParams<{ appraisalId: string; promptId: string }>();
  const { authenticatedFetch } = useAuthenticatedFetch();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [appraisalName, setAppraisalName] = useState<string>('');
  const [blurbDetail, setBlurbDetail] = useState<BlurbDetail | null>(null);
  const [editedPrompt, setEditedPrompt] = useState('');
  const [editedBlurb, setEditedBlurb] = useState('');
  const [llmModels, setLlmModels] = useState<Array<{ id: number; modelName: string; provider: string; family: string }>>([]);
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [operationId, setOperationId] = useState<string | null>(null);
  const [regenerationProgress, setRegenerationProgress] = useState<string>('');

  useEffect(() => {
    fetchAppraisal();
    fetchBlurbDetail();
    fetchLlmModels();
  }, [appraisalId, promptId]);

  useEffect(() => {
    // Poll operation status when regenerating
    if (operationId && regenerating) {
      const pollInterval = setInterval(() => {
        pollOperationStatus(operationId);
      }, 2000); // Poll every 2 seconds

      return () => clearInterval(pollInterval);
    }
  }, [operationId, regenerating]);

  const fetchAppraisal = async () => {
    if (!appraisalId) return;

    try {
      const response = await authenticatedFetch(`${API_URL}/api/appraisals/${appraisalId}`);

      if (!response || !response.ok) {
        console.error('Failed to fetch appraisal name');
        return;
      }

      const data = await response.json();

      if (data.success && data.data) {
        // Handle array values by taking the first element
        const name = Array.isArray(data.data.name) ? data.data.name[0] : data.data.name;
        setAppraisalName(name || 'Appraisal');
      }
    } catch (err) {
      console.error('Error fetching appraisal:', err);
      setAppraisalName('Appraisal');
    }
  };

  const fetchBlurbDetail = async () => {
    if (!appraisalId || !promptId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/appraisals/${appraisalId}/property-blurbs/${promptId}`
      );

      if (!response) {
        throw new Error('Failed to fetch blurb detail: No response received');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Failed to fetch blurb detail: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success && data.data) {
        setBlurbDetail(data.data);
        setEditedPrompt(data.data.prompt || '');
        setEditedBlurb(data.data.blurb || '');
      } else {
        throw new Error('Failed to load blurb detail');
      }
    } catch (err) {
      console.error('Error fetching blurb detail:', err);
      setError(err instanceof Error ? err.message : 'Failed to load blurb detail');
    } finally {
      setLoading(false);
    }
  };

  const fetchLlmModels = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/api/marketcompass/admin/models`);

      if (!response || !response.ok) {
        return; // Silently fail
      }

      const data = await response.json();

      if (data.success && data.data && data.data.models) {
        setLlmModels(data.data.models.map((model: any) => ({
          id: model.id,
          modelName: model.modelName,
          provider: model.provider,
          family: model.family
        })));

        if (data.data.models.length > 0) {
          setSelectedModel(data.data.models[0].modelName);
        }
      }
    } catch (err) {
      console.error('Error fetching LLM models:', err);
    }
  };

  const handleRegenerateBlurb = async () => {
    if (!appraisalId || !promptId) return;

    setRegenerating(true);
    setRegenerationProgress('Starting blurb regeneration...');
    setError(null);

    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/appraisals/${appraisalId}/property-blurbs/${promptId}/regenerate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            model_name: selectedModel,
            prompt: editedPrompt
          }),
        }
      );

      if (!response) {
        throw new Error('Failed to start regeneration: No response received');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Failed to start regeneration: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success && data.operationId) {
        setOperationId(data.operationId);
        setRegenerationProgress('Regeneration started...');
      } else {
        throw new Error('Failed to start regeneration');
      }
    } catch (err) {
      console.error('Error starting regeneration:', err);
      setError(err instanceof Error ? err.message : 'Failed to start regeneration');
      setRegenerating(false);
    }
  };

  const pollOperationStatus = async (opId: string) => {
    try {
      const response = await authenticatedFetch(`${API_URL}/api/operations/${opId}`);

      if (!response || !response.ok) {
        return;
      }

      const data = await response.json();

      if (data.success && data.operation) {
        const status = data.operation.status;
        const progress = data.operation.progress_text || data.operation.progress?.step || '';

        setRegenerationProgress(progress);

        if (status === 'completed') {
          setRegenerating(false);
          setOperationId(null);
          setRegenerationProgress('');
          // Refresh the blurb detail
          await fetchBlurbDetail();
        } else if (status === 'error') {
          setRegenerating(false);
          setOperationId(null);
          setError(data.operation.error_message || 'Regeneration failed');
          setRegenerationProgress('');
        }
      }
    } catch (err) {
      console.error('Error polling operation status:', err);
    }
  };

  const handleSave = async () => {
    if (!appraisalId || !blurbDetail?.blurb_record_id) return;

    setSaving(true);
    setError(null);

    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/appraisals/${appraisalId}/property-blurbs/${blurbDetail.blurb_record_id}/update`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            blurb: editedBlurb
          }),
        }
      );

      if (!response) {
        throw new Error('Failed to save: No response received');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Failed to save: ${response.statusText}`);
      }

      // Navigate back to appraisal page with LLM Blurbs tab selected
      navigate(`/appraisals/${appraisalId}/edit?tab=9`);
    } catch (err) {
      console.error('Error saving:', err);
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(`/appraisals/${appraisalId}/edit?tab=9`);
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!blurbDetail) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">Blurb not found</Alert>
          <Button onClick={handleBack} sx={{ mt: 2 }}>
            Back to Appraisal
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Link color="inherit" href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }}>
            Home
          </Link>
          <Link color="inherit" href="/appraisals" onClick={(e) => { e.preventDefault(); navigate('/appraisals'); }}>
            Appraisals
          </Link>
          <Link color="inherit" href={`/appraisals/${appraisalId}/edit?tab=9`} onClick={(e) => { e.preventDefault(); handleBack(); }}>
            {appraisalName || 'Appraisal'}
          </Link>
          <Typography color="text.primary">{blurbDetail.prompt_name}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">
            {blurbDetail.prompt_name}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back to Appraisal
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {regenerating && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">
                {regenerationProgress || 'Regenerating blurb...'}
              </Typography>
            </Box>
          </Alert>
        )}

        {/* Top Half - Prompt Information */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Prompt Information
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Prompt ID
              </Typography>
              <Typography variant="body1">{blurbDetail.prompt_id}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Process Order
              </Typography>
              <Typography variant="body1">{blurbDetail.process_order}</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Prompt"
                multiline
                rows={6}
                fullWidth
                value={editedPrompt}
                onChange={(e) => setEditedPrompt(e.target.value)}
                variant="outlined"
              />
            </Grid>
            {blurbDetail.good_example && (
              <Grid item xs={12}>
                <TextField
                  label="Good Example"
                  multiline
                  rows={3}
                  fullWidth
                  value={blurbDetail.good_example}
                  variant="outlined"
                  disabled
                />
              </Grid>
            )}
            {blurbDetail.bad_example && (
              <Grid item xs={12}>
                <TextField
                  label="Bad Example"
                  multiline
                  rows={3}
                  fullWidth
                  value={blurbDetail.bad_example}
                  variant="outlined"
                  disabled
                />
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Bottom Half - Blurb Information */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Generated Blurb
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 250 }} disabled={regenerating}>
                <InputLabel>LLM Model</InputLabel>
                <Select
                  value={selectedModel}
                  label="LLM Model"
                  onChange={(e) => setSelectedModel(e.target.value)}
                >
                  {llmModels.map((model) => (
                    <MenuItem key={model.id} value={model.modelName}>
                      {model.provider} - {model.family} ({model.modelName})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AutoAwesomeIcon />}
                onClick={handleRegenerateBlurb}
                disabled={regenerating || !selectedModel}
              >
                Regenerate Blurb
              </Button>
            </Box>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Generated Blurb"
                multiline
                rows={10}
                fullWidth
                value={editedBlurb}
                onChange={(e) => setEditedBlurb(e.target.value)}
                variant="outlined"
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving || regenerating}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};
